import React, {createContext, useCallback, useContext, useEffect, useRef, useState, useMemo, FC, ReactNode} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {
  getSubscriptionId,
  getSubscriptionDetails,
  setActiveSubscription,
} from '@/app/modules/widgets/core/_requests'
import {
  getUserSubscriptions,
  getSubscriptionDiscovery,
  DiscoveredSubscription,
  MembershipSubscription,
} from '@/app/modules/membership/api'
import {getLogger} from '@/shared/utils/logUtils'
import {SubscriptionDetails, ChatAccessDeniedReason} from '@/shared/utils/crud-helper/models'
import {computeChatAccess} from './chatAccess'
import {getSubscriptionLifecycle, type SubscriptionLifecycle} from './subscriptionLifecycle'
import {pickMembershipToActivate} from './membershipActivation'
import {SubscriptionDiscoveryModal} from '@/app/modules/membership/SubscriptionDiscoveryModal'
import {balanceQueryKey, useBalance} from './hooks/useAPIFiles'
import {useLastKnownBalance} from './hooks/useLastKnownBalance'
import {canStartChatTurn as canStartTurnFor, getCloudUnitsStatus, type CloudUnitsStatus} from './cloudUnitsStatus'

export type MembershipRole = 'owner' | 'admin' | 'member' | null

interface SubscriptionIdResult {
  /** The active subscription id (billing row), or null when the user has none. */
  id: string | null
  /** True when `GET /associate2` failed, so "no id" is unknown rather than a fact. */
  failed: boolean
}

interface Deferred {
  promise: Promise<void>
  resolve: () => void
}

const createDeferred = (): Deferred => {
  let resolve: () => void = () => undefined
  const promise = new Promise<void>((r) => {
    resolve = r
  })
  return {promise, resolve}
}

/**
 * Result of a one-shot discovery call, returned to callers of
 * `discoverSubscriptions()` so manual triggers (the "Check for additional
 * subscriptions" button) can surface precise feedback: "N found", "nothing
 * found", or "upstream unavailable". The auto-link branch never returns
 * `found > 0` — those entries are linked silently and never become
 * `discoveredSubscriptions`.
 */
export interface DiscoveryResult {
  found: number
  autoLinked: number
  warning: boolean
}

interface SubscriptionContextType {
  subscriptionId: string | null
  subscriptionDetails: SubscriptionDetails | null
  isLoading: boolean
  isLoadingDetails: boolean
  error: string | null
  hasSubscription: boolean
  /** End-date lifecycle of the active subscription; `null` until details load. */
  subscriptionLifecycle: SubscriptionLifecycle | null
  /**
   * The user's own storage (My Files) is available: a subscription is linked and it has not lapsed
   * (expired more than the grace period ago). Folders shared with the user are separate.
   */
  hasFileAccess: boolean
  /** Available cloud units (shared `useBalance` query cache); `null` while unknown. */
  balance: number | null
  cloudUnitsStatus: CloudUnitsStatus
  /**
   * False when the chat may open but must not start new turns: no cloud units left, or the
   * subscription only grants read-only history (`canViewAiChat` without `canUseAiChat`).
   */
  canStartChatTurn: boolean
  /** Hard gate: OmicsBox subscription present, enabled, not expired, licenses ENGINE. Balance is separate. */
  canUseAiChat: boolean
  /** The chat panel may open and history can be browsed (also true for an expired ENGINE subscription). */
  canViewAiChat: boolean
  chatAccessDeniedReason: ChatAccessDeniedReason | null
  role: MembershipRole
  maxSeats: number
  usedSeats: number
  /** All subscriptions this user is associated with (via CloudUsersRestrictions GSI1). */
  linkedSubscriptions: MembershipSubscription[]
  /** True while a switch-subscription call is in flight. */
  isSwitching: boolean
  /**
   * Subscriptions discovered upstream that the user could link but has not.
   * Empty when the user already linked everything, or when the most recent
   * discovery call auto-linked all matches silently. The post-login flow
   * populates this only when the user already has an active subscription —
   * when they have none, discovered entries are auto-linked instead.
   */
  discoveredSubscriptions: DiscoveredSubscription[]
  /** True while any discovery / auto-link / manual-link call is in flight. */
  isDiscovering: boolean
  refetch: () => Promise<void>
  /**
   * Set the balance from a value the backend already knows (the chat agent's `balanceRemaining`).
   * Preferred over refetching right after a turn: charges land asynchronously, so `GET /balance`
   * can still return the pre-charge value.
   */
  applyBalance: (units: number) => void
  /** Refetch the balance from `GET /balance` (e.g. after a recharge). */
  refreshBalance: () => Promise<void>
  /**
   * Re-read the active subscription id from the API, then reload details,
   * balance, and membership list. Use after a transient /subscription-details
   * failure or when the UI showed "details unavailable" while a key exists.
   */
  reloadSubscriptionDetails: () => Promise<void>
  /**
   * Switch the user's active (primary) subscription by its full Susy key.
   * Rewrites the USER#/USER# row on the backend and refetches all
   * subscription-dependent state (id, details, balance, members).
   */
  switchSubscription: (skey: string) => Promise<void>
  /**
   * Check the upstream subscription system for keys this user owns but has
   * not yet linked. When the user has no active subscription, any matches
   * are auto-linked silently; otherwise matches are stored in
   * `discoveredSubscriptions` for the UI to surface. Always safe to call.
   */
  discoverSubscriptions: () => Promise<DiscoveryResult>
  /**
   * Link one discovered subscription (user-initiated from the modal). On
   * success the entry is removed from `discoveredSubscriptions` and the
   * subscription switcher / details are refetched.
   */
  linkDiscoveredSubscription: (skey: string) => Promise<void>
  /** Drop any pending discovered entries (user dismissed the modal). */
  clearDiscoveredSubscriptions: () => void
}

const SubscriptionContext = createContext<SubscriptionContextType>({
  subscriptionId: null,
  subscriptionDetails: null,
  isLoading: true,
  isLoadingDetails: false,
  error: null,
  hasSubscription: false,
  subscriptionLifecycle: null,
  hasFileAccess: false,
  balance: null,
  cloudUnitsStatus: 'unknown',
  canStartChatTurn: true,
  canUseAiChat: false,
  canViewAiChat: false,
  chatAccessDeniedReason: null,
  role: null,
  maxSeats: 1,
  usedSeats: 0,
  linkedSubscriptions: [],
  isSwitching: false,
  discoveredSubscriptions: [],
  isDiscovering: false,
  refetch: async () => {},
  applyBalance: () => {},
  refreshBalance: async () => {},
  reloadSubscriptionDetails: async () => {},
  switchSubscription: async () => {},
  discoverSubscriptions: async () => ({found: 0, autoLinked: 0, warning: false}),
  linkDiscoveredSubscription: async () => {},
  clearDiscoveredSubscriptions: () => {},
})

export const useSubscription = () => {
  const context = useContext(SubscriptionContext)
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider')
  }
  return context
}

interface SubscriptionProviderProps {
  children: ReactNode
}

export const SubscriptionProvider: FC<SubscriptionProviderProps> = ({children}) => {
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null)
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [role, setRole] = useState<MembershipRole>(null)
  const [maxSeats, setMaxSeats] = useState(1)
  const [usedSeats, setUsedSeats] = useState(0)
  const [linkedSubscriptions, setLinkedSubscriptions] = useState<MembershipSubscription[]>([])
  const [isSwitching, setIsSwitching] = useState(false)
  const [discoveredSubscriptions, setDiscoveredSubscriptions] = useState<DiscoveredSubscription[]>([])
  const [isDiscovering, setIsDiscovering] = useState(false)
  // True after the first post-login discovery attempt has completed, so we
  // do not re-trigger auto-discovery every time subscriptionId changes (e.g.
  // after switchSubscription refetches).
  const autoDiscoveryDone = useRef(false)
  /** Bumps on each `subscriptionId` effect run so in-flight work from a previous key can be ignored. */
  const subscriptionLoadGenRef = useRef(0)
  /**
   * Latest memberships, readable from async code that started before React committed them
   * (`linkedSubscriptions` in a closure can still be the initial `[]`).
   */
  const linkedSubscriptionsRef = useRef<MembershipSubscription[]>([])
  /**
   * Resolves once the memberships after sign-in are known (or could not be loaded). Discovery waits
   * for it: with an empty list it would silently link every Salesforce key it finds, racing the
   * provider's own activation of a membership for the billing row.
   */
  const membershipsLoadedRef = useRef<Deferred>(createDeferred())

  /**
   * @param hasActiveSubscription - Whether the user has an active subscription. Without one, role
   *   and seats stay empty unless a membership is flagged primary: an arbitrary first membership must
   *   not grant its role (e.g. owner) while nothing is active.
   */
  const applyLinkedSubscriptions = (subs: MembershipSubscription[], hasActiveSubscription = true) => {
    linkedSubscriptionsRef.current = subs
    setLinkedSubscriptions(subs)
    const primary = subs.find((s) => s.is_primary) ?? (hasActiveSubscription ? subs[0] : undefined)
    if (primary) {
      setRole(primary.role)
      setMaxSeats(primary.max_seats)
      setUsedSeats(primary.used_seats)
    } else {
      setRole(null)
      setMaxSeats(1)
      setUsedSeats(0)
    }
  }

  /** Loads the active subscription id (the billing row). Never throws; see `SubscriptionIdResult`. */
  const fetchSubscriptionId = async (): Promise<SubscriptionIdResult> => {
    try {
      setIsLoading(true)
      setError(null)

      const id = await getSubscriptionId()
      setSubscriptionId(id || null)

      if (!id) {
        getLogger().warn('No subscription ID available')
      } else {
        getLogger().info('Subscription ID loaded successfully')
      }
      return {id: id || null, failed: false}
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch subscription info'
      setError(errorMessage)
      getLogger().error('Error fetching subscription info:', err)
      return {id: null, failed: true}
    } finally {
      setIsLoading(false)
    }
  }

  const fetchMembershipInfo = async (hasActiveSubscription = true): Promise<MembershipSubscription[]> => {
    try {
      const response = await getUserSubscriptions()
      const subs = response.subscriptions ?? []
      // Pull role/seats from the primary (currently linked) subscription so that
      // the values stay in sync with subscriptionId when the user switches.
      applyLinkedSubscriptions(subs, hasActiveSubscription)
      return subs
    } catch (err) {
      getLogger().warn('Failed to fetch membership info (v1 endpoint may not be deployed yet):', err)
      return []
    }
  }

  /**
   * A user can be a member of a key without an active subscription (no billing row): added to the key
   * by hand, or before ever linking it. `GET /associate2` then has no id, and the `subscriptionId`
   * effect that loads memberships never runs, so Settings had nothing to offer (#419). Load the
   * memberships anyway so Settings can list them, and activate the only usable one: `POST
   * /associate2` on an existing membership writes the billing row.
   */
  const activateMembershipWithoutSubscription = async (isCancelled: () => boolean) => {
    const subs = await fetchMembershipInfo(false)
    membershipsLoadedRef.current.resolve()
    const target = pickMembershipToActivate(subs)
    if (!target || isCancelled()) {
      return
    }
    try {
      await setActiveSubscription(target.skey)
      getLogger().info(`Activated linked membership ${target.subscription_id} (no active subscription)`)
      await fetchSubscriptionId()
    } catch (err) {
      getLogger().warn('Could not activate the linked membership:', err)
    }
  }

  const fetchSubscriptionDetails = async () => {
    try {
      setIsLoadingDetails(true)
      const details = await getSubscriptionDetails()
      setSubscriptionDetails(details)
      getLogger().info('Subscription details loaded successfully')
    } catch (err) {
      getLogger().error('Failed to fetch subscription details:', err)
      setSubscriptionDetails(null)
    } finally {
      setIsLoadingDetails(false)
    }
  }

  const queryClient = useQueryClient()
  const balanceQuery = useBalance(subscriptionId)
  const {balance, remember: rememberBalance} = useLastKnownBalance(subscriptionId, balanceQuery.data)

  const applyBalance = useCallback(
    (units: number) => {
      rememberBalance(units)
      if (subscriptionId) {
        queryClient.setQueryData(balanceQueryKey(subscriptionId), units)
      }
    },
    [queryClient, rememberBalance, subscriptionId]
  )

  const refreshBalance = useCallback(async () => {
    if (subscriptionId) {
      await queryClient.invalidateQueries(balanceQueryKey(subscriptionId))
    }
  }, [queryClient, subscriptionId])

  useEffect(() => {
    let isCancelled = false

    const loadSubscription = async () => {
      if (isCancelled) {
        return
      }
      const {id, failed} = await fetchSubscriptionId()
      if (failed || isCancelled) {
        // Unknown state: never activate anything on a failed lookup (it could overwrite a billing
        // row that points at another key). Discovery must not wait forever either.
        membershipsLoadedRef.current.resolve()
        return
      }
      if (!id) {
        await activateMembershipWithoutSubscription(() => isCancelled)
      }
      // With an id, the `subscriptionId` effect loads the memberships and resolves the wait.
    }

    loadSubscription()

    return () => {
      isCancelled = true
    }
    // Runs once per mount (sign-in); the loaders close over state setters only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!subscriptionId) {
      return
    }
    const myGen = (subscriptionLoadGenRef.current += 1)

    void (async () => {
      // Fan out the independent fetches in parallel (the balance is the `useBalance` query). The
      // generation guard still prevents stale writes when subscriptions are switched rapidly — we
      // check once after all promises settle.
      setIsLoadingDetails(true)

      const [detailsResult, membershipResult] = await Promise.allSettled([
        getSubscriptionDetails(), getUserSubscriptions(),
      ])

      if (subscriptionLoadGenRef.current !== myGen) {
        return
      }

      // Apply results — each branch handles its own success/failure.
      if (detailsResult.status === 'fulfilled') {
        setSubscriptionDetails(detailsResult.value)
        getLogger().info('Subscription details loaded successfully')
      } else {
        getLogger().error('Failed to fetch subscription details:', detailsResult.reason)
        setSubscriptionDetails(null)
      }
      setIsLoadingDetails(false)

      let subs: MembershipSubscription[] = []
      if (membershipResult.status === 'fulfilled') {
        subs = membershipResult.value.subscriptions ?? []
        applyLinkedSubscriptions(subs)
      } else {
        getLogger().warn('Failed to fetch membership info:', membershipResult.reason)
      }
      membershipsLoadedRef.current.resolve()

      // One-shot post-login discovery — fire-and-forget so it does not block
      // the settings page from becoming interactive. The role/seats/members
      // are already set above; discovery only populates the modal for
      // additional unlinked keys.
      if (!autoDiscoveryDone.current) {
        autoDiscoveryDone.current = true
        runDiscovery(subs)
          .catch((err) =>
            getLogger().warn('Post-login discovery failed:', err)
          )
      }
    })()
    // runDiscovery closes over setters; deps intentionally just subscriptionId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionId])

  const refetch = async () => {
    await fetchSubscriptionId()
  }

  const reloadSubscriptionDetails = async () => {
    try {
      setError(null)
      const id = await getSubscriptionId()
      setSubscriptionId(id || null)
      if (!id) {
        setSubscriptionDetails(null)
        await fetchMembershipInfo(false)
        return
      }
      await Promise.all([
        fetchSubscriptionDetails(),
        queryClient.invalidateQueries(balanceQueryKey(id)),
        fetchMembershipInfo(),
      ])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reload subscription'
      setError(errorMessage)
      getLogger().error('reloadSubscriptionDetails failed:', err)
    }
  }

  /**
   * Inner discovery routine that operates on an explicit `currentLinked`
   * snapshot — using the state value directly is unsafe here because the
   * post-login flow triggers discovery immediately after membership fetch,
   * before React has committed the new `linkedSubscriptions` value.
   */
  const runDiscovery = async (
    currentLinked: MembershipSubscription[]
  ): Promise<DiscoveryResult> => {
    setIsDiscovering(true)
    try {
      const resp = await getSubscriptionDiscovery()
      const warning = Array.isArray(resp.warnings) && resp.warnings.length > 0
      const discoverable = resp.discoverable ?? []
      if (discoverable.length === 0) {
        setDiscoveredSubscriptions([])
        return {found: 0, autoLinked: 0, warning}
      }

      const hasActiveLinked = currentLinked.some(
        (s) => s.is_enabled !== false && !s.is_expired
      )

      if (!hasActiveLinked) {
        // Silent auto-link path: the user has no active subscription, so just
        // attach everything we found. Each /associate2 call rewrites the
        // USER#/USER# billing row, so the last successful link becomes the
        // active subscription — for the common case of a single newly
        // purchased key this is exactly what the user expects.
        let linked = 0
        for (const entry of discoverable) {
          try {
            await setActiveSubscription(entry.skey)
            linked += 1
            getLogger().info(`Auto-linked discovered subscription ${entry.subscription_id}`)
          } catch (err) {
            getLogger().warn(
              `Auto-link failed for discovered subscription ${entry.subscription_id}:`,
              err
            )
          }
        }
        setDiscoveredSubscriptions([])
        if (linked > 0) {
          // Refetch subscription state so the UI reflects the new primary key.
          await fetchSubscriptionId()
        }
        return {found: discoverable.length, autoLinked: linked, warning}
      }

      // User already has an active subscription — let the UI surface a modal
      // so they can decide which (if any) additional keys to link.
      setDiscoveredSubscriptions(discoverable)
      return {found: discoverable.length, autoLinked: 0, warning}
    } catch (err) {
      getLogger().warn('Subscription discovery failed:', err)
      setDiscoveredSubscriptions([])
      return {found: 0, autoLinked: 0, warning: true}
    } finally {
      setIsDiscovering(false)
    }
  }

  const discoverSubscriptions = async (): Promise<DiscoveryResult> => {
    // Settings calls this on mount, usually before the memberships are in: wait for them, then use
    // the latest list (the render closure may still hold the initial empty one).
    await membershipsLoadedRef.current.promise
    return runDiscovery(linkedSubscriptionsRef.current)
  }

  const linkDiscoveredSubscription = async (skey: string): Promise<void> => {
    setIsDiscovering(true)
    try {
      await setActiveSubscription(skey)
      getLogger().info(`Linked discovered subscription (skey ending ...${skey.slice(-4)})`)
      // Drop the linked entry from the pending list regardless of what the
      // refetch finds, so the modal does not re-show the same row.
      setDiscoveredSubscriptions((prev) => prev.filter((s) => s.skey !== skey))
      await fetchSubscriptionId()
    } finally {
      setIsDiscovering(false)
    }
  }

  const clearDiscoveredSubscriptions = () => {
    setDiscoveredSubscriptions([])
  }

  const switchSubscription = async (skey: string) => {
    const target = linkedSubscriptions.find((s) => s.skey === skey)
    if (!target) {
      throw new Error('Cannot switch: subscription not in user\'s linked list')
    }
    if (target.is_primary) {
      return
    }
    try {
      setIsSwitching(true)
      setError(null)
      await setActiveSubscription(skey)
      getLogger().info(`Switched active subscription to ${target.subscription_id}`)
      // Optimistically flip is_primary in the local list so the subscription
      // switcher updates the moment the backend confirms the change. Without
      // this the dropdown keeps showing the old active entry for the ~300-700ms
      // it takes fetchMembershipInfo (triggered by the subscriptionId effect
      // below) to round-trip, which reads as a confusing "still on the old
      // one" flash. The subsequent refetch re-affirms this state.
      linkedSubscriptionsRef.current = linkedSubscriptionsRef.current.map((s) => ({
        ...s,
        is_primary: s.skey === skey,
      }))
      setLinkedSubscriptions(linkedSubscriptionsRef.current)
      // Keep role and seat counts in sync with the key we just selected; without
      // this, is_primary flips immediately but role/max/used stay on the old
      // subscription until getUserSubscriptions runs (end of the subscriptionId
      // effect), and Manage Users shows the wrong people and numbers.
      setRole(target.role)
      setMaxSeats(target.max_seats)
      setUsedSeats(target.used_seats)
      // Clear dependent state so stale details don't flash during refetch (the balance query is
      // keyed by subscription id, so it switches on its own).
      setSubscriptionDetails(null)
      // Refetching the id will trigger the effect that reloads details, balance,
      // and membership info for the newly active subscription.
      await fetchSubscriptionId()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to switch subscription'
      setError(message)
      getLogger().error('Error switching subscription:', err)
      throw err
    } finally {
      setIsSwitching(false)
    }
  }

  const hasSubscription = Boolean(subscriptionId && !error)
  const subscriptionLifecycle = subscriptionDetails
    ? getSubscriptionLifecycle(subscriptionDetails.endDate)
    : null
  // Unknown details (still loading, transient failure) keep files available rather than locking out.
  const hasFileAccess = hasSubscription && subscriptionLifecycle !== 'lapsed'

  const {canUseAiChat, canViewAiChat, chatAccessDeniedReason} = useMemo(
    () => {
      const {canUse, canView, reason} = computeChatAccess(subscriptionId, subscriptionDetails, error)
      return {canUseAiChat: canUse, canViewAiChat: canView, chatAccessDeniedReason: reason}
    },
    [subscriptionId, subscriptionDetails, error]
  )

  const value: SubscriptionContextType = {
    subscriptionId,
    subscriptionDetails,
    isLoading,
    isLoadingDetails,
    error,
    hasSubscription,
    subscriptionLifecycle,
    hasFileAccess,
    balance,
    cloudUnitsStatus: getCloudUnitsStatus(balance),
    canStartChatTurn: canUseAiChat && canStartTurnFor(balance),
    canUseAiChat,
    canViewAiChat,
    chatAccessDeniedReason,
    role,
    maxSeats,
    usedSeats,
    linkedSubscriptions,
    isSwitching,
    discoveredSubscriptions,
    isDiscovering,
    refetch,
    applyBalance,
    refreshBalance,
    reloadSubscriptionDetails,
    switchSubscription,
    discoverSubscriptions,
    linkDiscoveredSubscription,
    clearDiscoveredSubscriptions,
  }

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
      <SubscriptionDiscoveryModal />
    </SubscriptionContext.Provider>
  )
}
